import React from 'react';
import styled from 'styled-components';

const PageContainer = styled.div`
  min-height: 100vh;
  padding: 6rem 2rem 4rem;
  background: linear-gradient(135deg, var(--color-background) 0%, #1a1a1a 100%);

  @media (max-width: 768px) {
    padding: 5rem 1rem 3rem;
  }
`;

const ContentWrapper = styled.div`
  max-width: 900px;
  margin: 0 auto;
  background: rgba(255, 255, 255, 0.95);
  padding: 3rem;
  border-radius: 30px;
  border: 4px solid var(--color-primary);
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);

  @media (max-width: 768px) {
    padding: 2rem 1.5rem;
  }
`;

const Title = styled.h1`
  font-family: var(--font-display);
  font-size: 3rem;
  color: var(--color-primary);
  text-align: center;
  margin-bottom: 1rem;
  text-transform: uppercase;
  letter-spacing: 0.1em;

  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const LastUpdated = styled.p`
  text-align: center;
  color: #666;
  font-size: 0.9rem;
  margin-bottom: 2rem;
  font-weight: 600;
`;

const Section = styled.section`
  margin-bottom: 2.5rem;
`;

const SectionTitle = styled.h2`
  font-family: var(--font-display);
  font-size: 1.8rem;
  color: var(--color-accent);
  margin-bottom: 1rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;

  @media (max-width: 768px) {
    font-size: 1.4rem;
  }
`;

const Paragraph = styled.p`
  color: #1a1a1a;
  line-height: 1.8;
  margin-bottom: 1rem;
  font-family: var(--font-body);
  font-size: 1rem;
  font-weight: 500;
`;

const List = styled.ul`
  color: #1a1a1a;
  line-height: 1.8;
  margin-left: 2rem;
  margin-bottom: 1rem;

  li {
    margin-bottom: 0.5rem;
    font-weight: 500;
  }
`;

const ContactInfo = styled.div`
  background: rgba(255, 229, 92, 0.1);
  padding: 1.5rem;
  border-radius: 15px;
  border: 2px solid var(--color-primary);
  margin-top: 2rem;
  text-align: center;

  a {
    color: var(--color-primary);
    font-weight: 700;
    text-decoration: underline;
  }
`;

const TermsPage: React.FC = () => {
  return (
    <PageContainer>
      <ContentWrapper>
        <Title>Terms & Conditions</Title>
        <LastUpdated>Last Updated: December 3, 2025</LastUpdated>

        <Section>
          <SectionTitle>1. Acceptance of Terms</SectionTitle>
          <Paragraph>
            Welcome to Pepe's Dog Army! By accessing and using this website, you accept and agree to be bound
            by the terms and provisions of this agreement. If you do not agree to these terms, please do
            not use this website.
          </Paragraph>
        </Section>

        <Section>
          <SectionTitle>2. About Pepe's Dog Army</SectionTitle>
          <Paragraph>
            Pepe's Dog Army is a community-driven initiative celebrating ZEUS token holders and Pepe's Dog enthusiasts.
            We are NOT financial advisors, investment professionals, or any form of licensed financial entity.
            We are a community of cryptocurrency enthusiasts sharing information and building together.
          </Paragraph>
        </Section>

        <Section>
          <SectionTitle>3. No Financial Advice</SectionTitle>
          <Paragraph>
            Nothing on this website constitutes financial, investment, legal, or tax advice. All content is
            for informational and entertainment purposes only. You should:
          </Paragraph>
          <List>
            <li>Always conduct your own research (DYOR)</li>
            <li>Consult with qualified financial professionals before making investment decisions</li>
            <li>Never invest more than you can afford to lose</li>
            <li>Understand that cryptocurrency investments carry significant risk</li>
          </List>
        </Section>

        <Section>
          <SectionTitle>4. Risk Disclosure</SectionTitle>
          <Paragraph>
            Cryptocurrency investments are highly speculative and volatile. You may lose all invested capital.
            Past performance does not guarantee future results. The ZEUS token and related activities involve
            risks including but not limited to:
          </Paragraph>
          <List>
            <li>Market volatility and price fluctuations</li>
            <li>Regulatory changes</li>
            <li>Technical vulnerabilities</li>
            <li>Loss of access to wallets or private keys</li>
            <li>Smart contract risks</li>
          </List>
        </Section>

        <Section>
          <SectionTitle>5. User Responsibilities</SectionTitle>
          <Paragraph>
            As a user of this website, you agree to:
          </Paragraph>
          <List>
            <li>Provide accurate information when required</li>
            <li>Maintain the security of your wallet and private keys</li>
            <li>Comply with all applicable laws and regulations</li>
            <li>Not engage in any fraudulent or illegal activities</li>
            <li>Respect intellectual property rights</li>
          </List>
        </Section>

        <Section>
          <SectionTitle>6. Intellectual Property</SectionTitle>
          <Paragraph>
            All content on this website, including text, graphics, logos, and images, is the property of
            Pepe's Dog Army or its content suppliers and is protected by copyright laws. The ZEUS brand and
            associated materials belong to their respective owners.
          </Paragraph>
        </Section>

        <Section>
          <SectionTitle>7. Third-Party Links</SectionTitle>
          <Paragraph>
            This website may contain links to third-party websites. We are not responsible for the content,
            accuracy, or practices of these external sites. Use of third-party websites is at your own risk.
          </Paragraph>
        </Section>

        <Section>
          <SectionTitle>8. Limitation of Liability</SectionTitle>
          <Paragraph>
            To the fullest extent permitted by law, Pepe's Dog Army and its contributors shall not be liable for
            any direct, indirect, incidental, consequential, or punitive damages arising from your use of
            this website or participation in the community.
          </Paragraph>
        </Section>

        <Section>
          <SectionTitle>9. Changes to Terms</SectionTitle>
          <Paragraph>
            We reserve the right to modify these terms at any time. Continued use of the website after
            changes constitutes acceptance of the modified terms. We will update the "Last Updated" date
            to reflect any changes.
          </Paragraph>
        </Section>

        <Section>
          <SectionTitle>10. Governing Law</SectionTitle>
          <Paragraph>
            These terms shall be governed by and construed in accordance with applicable laws, without
            regard to conflict of law provisions.
          </Paragraph>
        </Section>

        <Section>
          <SectionTitle>11. Community Standards</SectionTitle>
          <Paragraph>
            Pepe's Dog Army is built on respect, transparency, and fun. We expect all community members to:
          </Paragraph>
          <List>
            <li>Treat others with respect and kindness</li>
            <li>Avoid spam, scams, and misleading information</li>
            <li>Contribute positively to discussions</li>
            <li>Follow the pack mentality - we succeed together! 🐶</li>
          </List>
        </Section>

        <ContactInfo>
          <Paragraph>
            <strong>Questions about these Terms?</strong><br />
            Contact us at: <a href="mailto:hello@pepesdog.box">hello@pepesdog.box</a>
          </Paragraph>
        </ContactInfo>
      </ContentWrapper>
    </PageContainer>
  );
};

export default TermsPage;
